// Sign-in: find your organization → username at its domain → password.
// /login/<slug> opens straight at an organization, and the last organization
// used on this device is remembered. "Sign in with your full email" skips the search.
import type { PublicOrg, User } from "../shared/types";
import { $, api, esc } from "./api";
import { hideMessage, setupPasswordToggles, showMessage } from "./ui";

interface SessionResult {
  user: User & { mustChangePassword: boolean };
}

const LAST_ORG = "kontour:lastOrg";
let org: PublicOrg | null = null;
let fullEmailMode = false;

const remember = (slug: string | null) => {
  try {
    slug ? localStorage.setItem(LAST_ORG, slug) : localStorage.removeItem(LAST_ORG);
  } catch {
    /* private browsing: fine */
  }
};
const remembered = (): string | null => {
  try {
    return localStorage.getItem(LAST_ORG);
  } catch {
    return null;
  }
};

const logoHtml = (o: PublicOrg) => `<img class="logo-box" src="${esc(o.pictureUrl)}" alt="">`;

setupPasswordToggles();
const params = new URLSearchParams(location.search);
if (params.has("reset")) showMessage("notice", "Your password is changed. Please sign in with your new password.");
if (params.has("activated")) showMessage("notice", "Your account is ready. Please sign in.");

// ---------- Step 1: find the organization ----------

const search = $<HTMLInputElement>("org-search");
const results = $("org-results");
let timer = 0;
let latest = 0;

search.addEventListener("input", () => {
  window.clearTimeout(timer);
  hideMessage("find-error");
  const q = search.value.trim();
  if (q.length < 3) {
    results.innerHTML = "";
    return;
  }
  timer = window.setTimeout(async () => {
    const mine = ++latest;
    try {
      const res = await api<{ results: PublicOrg[] }>(`/api/orgs/search?q=${encodeURIComponent(q)}`);
      if (mine !== latest) return;
      results.innerHTML = res.results.length
        ? res.results
            .map(
              (o, i) =>
                `<li><button type="button" class="org-option" role="option" data-i="${i}">${logoHtml(o)}<div><strong>${esc(o.name)}</strong><span>${esc(o.domains.join(", "))}</span></div></button></li>`,
            )
            .join("")
        : `<li class="hint">${
            q.includes("@")
              ? "No organization on Kontour uses that email's domain. Check the spelling, or ask your organization's admin."
              : "No listed organization matches. Try your email or your organization's web address."
          }</li>`;
      results.querySelectorAll<HTMLButtonElement>(".org-option").forEach((b) =>
        b.addEventListener("click", () => selectOrg(res.results[Number(b.dataset.i)]!)),
      );
    } catch (err) {
      showMessage("find-error", (err as Error).message);
    }
  }, 250);
});

$("use-email").addEventListener("click", () => {
  fullEmailMode = true;
  org = null;
  showSignIn();
});

// ---------- Steps 2 and 3: username, then password ----------

const username = $<HTMLInputElement>("username");
const password = $<HTMLInputElement>("password");
const domainSelect = $<HTMLSelectElement>("domain-select");
const submit = $<HTMLButtonElement>("submit");

function selectOrg(o: PublicOrg): void {
  org = o;
  fullEmailMode = false;
  // Found by typing an email: carry the username (and its domain) on.
  const typed = search.value.trim().toLowerCase();
  showSignIn();
  if (typed.includes("@")) {
    const [user, domain] = typed.split("@") as [string, string];
    username.value = user;
    if (o.domains.includes(domain)) domainSelect.value = domain;
  }
}

function showSignIn(): void {
  $("step-find").hidden = true;
  $("step-signin").hidden = false;
  $("org-head").hidden = !org;
  if (org) {
    $("org-name").textContent = org.name;
    const logo = $<HTMLImageElement>("org-logo");
    logo.hidden = false;
    logo.src = org.pictureUrl;
  }
  const domains = org?.domains ?? [];
  $("username-label").textContent = fullEmailMode ? "Email" : "Username";
  username.type = fullEmailMode ? "email" : "text";
  $("at-domain").textContent = !fullEmailMode && domains.length === 1 ? `@${domains[0]}` : "";
  domainSelect.hidden = fullEmailMode || domains.length < 2;
  domainSelect.innerHTML = domains.map((d) => `<option value="${esc(d)}">@${esc(d)}</option>`).join("");
  $("signin-title").textContent = org ? `Sign in to ${org.name}` : "Sign in";
  showUsernameStep();
}

function showUsernameStep(): void {
  $("username-field").hidden = false;
  $("password-field").hidden = true;
  submit.textContent = "Continue";
  hideMessage("error");
  username.focus();
}

function emailFromInput(): string {
  const value = username.value.trim().toLowerCase();
  if (value.includes("@") || fullEmailMode) return value;
  const domain = domainSelect.hidden ? org?.domains[0] : domainSelect.value;
  return domain ? `${value}@${domain}` : value;
}

$("change-org").addEventListener("click", () => {
  remember(null);
  org = null;
  $("step-signin").hidden = true;
  $("step-find").hidden = false;
  search.focus();
});
$("change-user").addEventListener("click", showUsernameStep);

$<HTMLFormElement>("form").addEventListener("submit", async (e) => {
  e.preventDefault();
  hideMessage("error");
  const email = emailFromInput();
  if (!$("password-field").hidden) return signIn(email);

  if (!username.value.trim()) return showMessage("error", fullEmailMode ? "Please enter your email." : "Please enter your username.");
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return showMessage("error", "That doesn't look like a valid email address.");
  if (fullEmailMode && !org) {
    // Show whose sign-in this is, when we know the domain.
    try {
      const found = await api<{ org: PublicOrg | null }>("/api/orgs/by-email", { method: "POST", body: { email } });
      if (found.org) {
        org = found.org;
        $("org-head").hidden = false;
        $("org-name").textContent = org.name;
        const logo = $<HTMLImageElement>("org-logo");
        logo.hidden = false;
        logo.src = org.pictureUrl;
      }
    } catch {
      /* not essential */
    }
  }
  $<HTMLInputElement>("email").value = email;
  $("signing-as").textContent = email;
  $("username-field").hidden = true;
  $("password-field").hidden = false;
  submit.textContent = "Sign in";
  password.focus();
});

async function signIn(email: string): Promise<void> {
  if (!password.value) return showMessage("error", "Please enter your password.");
  submit.disabled = true;
  try {
    const res = await api<SessionResult>("/api/login", { method: "POST", body: { email, password: password.value } });
    if (org) remember(org.slug);
    location.href = res.user.mustChangePassword ? "/change-password" : "/dashboard";
  } catch (err) {
    showMessage("error", err instanceof TypeError ? "Couldn't reach the server. Check your connection and try again." : (err as Error).message);
  } finally {
    submit.disabled = false;
  }
}

// ---------- Start: /login/<slug>, or the remembered organization ----------

async function start(): Promise<void> {
  const fromPath = location.pathname.match(/^\/login\/([a-z0-9-]{3,40})$/)?.[1];
  const slug = fromPath ?? remembered();
  if (!slug) return;
  try {
    selectOrg(await api<PublicOrg>(`/api/orgs/by-slug/${slug}`));
  } catch {
    if (!fromPath) remember(null);
    else showMessage("find-error", "We couldn't find that organization's sign-in page. Search for it below.");
  }
}
void start();

// ---------- Google and Microsoft (shown once their keys are set) ----------

const SSO_MESSAGES: Record<string, string> = {
  none: "There's no Kontour account for that email. Ask your organization's admin to add you, or sign in with your password.",
  unverified: "That account's email isn't confirmed by the provider, so we can't match it. Sign in with your password, then link it in your settings.",
  staff: "Gatehier staff accounts can't sign in this way.",
  suspended: "That account is suspended. Please contact your organization's admin or Gatehier.",
  expired: "That sign-in took too long or was already used. Please try again.",
  failed: "We couldn't finish signing you in. Please try again.",
  cancelled: "Signing in was cancelled.",
  off: "That way of signing in isn't available.",
};
const ssoProblem = new URLSearchParams(location.search).get("sso");
if (ssoProblem && SSO_MESSAGES[ssoProblem]) showMessage("find-error", SSO_MESSAGES[ssoProblem]!);
void api<{ google: boolean; microsoft: boolean }>("/api/auth/providers")
  .then((p) => {
    $("sso-google").hidden = !p.google;
    $("sso-microsoft").hidden = !p.microsoft;
    $("sso").hidden = !p.google && !p.microsoft;
  })
  .catch(() => {});
